import { useMemo } from 'react';
import {
  canAccessFeature,
  getRequiredTier,
  hasRole,
  maxSubscriptionTier,
  normalizeSubscriptionTier,
  resolveSubscriptionTierForDisplay,
  type Academy,
  type SubscriptionTier,
  TierFeatureKey,
} from '@court-sync/shared';
import { useSubscription } from './useSubscription';
import { useCurrentAcademy } from '@/hooks/useDataStore';
import { useOwnerStripeTier } from '@/queries/academyOwnerStripeTier';
import { useAuthStore } from '@/store/auth';

/**
 * Academy owner subscription tier from current plan (Stripe internalId + name), for feature gating.
 */
export function useFeatureAccess(feature: TierFeatureKey) {
  const { subscription, plans } = useSubscription();

  const currentTier = useMemo((): SubscriptionTier => {
    if (!subscription?.planId || !plans?.length) return 'starter';
    const plan = plans.find((p) => p.id === subscription.planId);
    if (!plan) return 'starter';
    const internalId = (plan as { internalId?: string | null }).internalId;
    const fromInternal = normalizeSubscriptionTier(internalId ?? null);
    if (fromInternal) return fromInternal;
    return resolveSubscriptionTierForDisplay(plan.name, internalId);
  }, [subscription, plans]);

  return {
    hasAccess: canAccessFeature(currentTier, feature),
    currentTier,
    requiredTier: getRequiredTier(feature),
  };
}

/**
 * Feature gates from the **academy's** `subscriptionTier` column only.
 * Prefer {@link useCoachAcademyFeatureAccess} for owner dashboard features when Stripe may be ahead of the DB (e.g. after upgrading plans).
 */
export function useAcademyFeatureAccess(
  academy: Academy | null | undefined,
  feature: TierFeatureKey
) {
  const currentTier = useMemo((): SubscriptionTier => {
    return normalizeSubscriptionTier(academy?.subscriptionTier ?? null) ?? 'starter';
  }, [academy?.subscriptionTier]);

  return {
    hasAccess: canAccessFeature(currentTier, feature),
    currentTier,
    requiredTier: getRequiredTier(feature),
  };
}

/**
 * Like {@link useAcademyFeatureAccess}, but merges the academy owner's **Stripe** plan
 * (via public `subscription-plan`) with `academy.subscriptionTier`.
 * Matches backend `resolveAcademyOwnerSubscriptionTier` so coaches see the same tier as owners
 * when the DB column is stale or unset.
 */
export function useCoachAcademyFeatureAccess(
  academy: Academy | null | undefined,
  academySlug: string | null | undefined,
  feature: TierFeatureKey
) {
  const { ownerStripeTier, status } = useOwnerStripeTier(academySlug);

  const fromAcademyTier = useMemo(
    () => normalizeSubscriptionTier(academy?.subscriptionTier ?? null) ?? 'starter',
    [academy?.subscriptionTier]
  );

  const tierResolved = !academySlug || status === 'success' || status === 'error';

  const currentTier = useMemo((): SubscriptionTier => {
    if (!ownerStripeTier) return fromAcademyTier;
    return maxSubscriptionTier(fromAcademyTier, ownerStripeTier);
  }, [fromAcademyTier, ownerStripeTier]);

  const hasAccessFromAcademyDb = canAccessFeature(fromAcademyTier, feature);

  return {
    hasAccess: canAccessFeature(currentTier, feature),
    hasAccessFromAcademyDb,
    currentTier,
    requiredTier: getRequiredTier(feature),
    /** True once Stripe tier fetch finished or skipped (no slug). */
    tierResolved,
  };
}

/**
 * Academy owner and delegated academy admin dashboards: use the **academy’s** effective tier
 * (DB `subscriptionTier` merged with the owner’s Stripe plan via the public subscription-plan API).
 * When the logged-in user is the academy owner, also merges their personal subscription tier so
 * upgrades unlock features immediately without waiting for academy cache / subscription-plan refetch.
 * Delegated admins still see gates from the academy owner’s plan only.
 */
export function useManagementDashboardFeatureAccess(feature: TierFeatureKey) {
  const { currentAcademy } = useCurrentAcademy();
  const { user } = useAuthStore();
  const isAcademyOwner = !!(user && hasRole(user, 'academy_owner'));
  const coachAccess = useCoachAcademyFeatureAccess(
    currentAcademy,
    currentAcademy?.slug ?? null,
    feature
  );
  const { currentTier: ownerPersonalTier, isLoading: subscriptionLoading } = useSubscription();

  const currentTier = useMemo((): SubscriptionTier => {
    if (!isAcademyOwner) return coachAccess.currentTier;
    return maxSubscriptionTier(coachAccess.currentTier, ownerPersonalTier);
  }, [coachAccess.currentTier, isAcademyOwner, ownerPersonalTier]);

  const hasAccessFromOwnerSubscription =
    isAcademyOwner && canAccessFeature(ownerPersonalTier, feature);

  const tierResolved =
    coachAccess.tierResolved && (!isAcademyOwner || !subscriptionLoading);

  return {
    hasAccess: canAccessFeature(currentTier, feature),
    hasAccessFromAcademyDb: coachAccess.hasAccessFromAcademyDb,
    hasAccessFromOwnerSubscription,
    currentTier,
    requiredTier: getRequiredTier(feature),
    tierResolved,
  };
}
